import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import i18n from "@/i18n";
import { authenticate } from "@/models/socket/authenticate.model";
import SocketController from "@/services/Socket/socket.controller";
import {
  EditableCompany,
  UserProfileResponse,
  userProfileResponseSchema,
} from "@/types";
import { convertEditableCompanyToDBExpectedFormat } from "@/utils/dataConversion";
import { validateWithSchema } from "@/utils/dataValidators.zod";
import {
  getLSRefreshToken,
  setSessionDismissedUpdateAccount,
} from "@/utils/tokens.util";

// #region fetchUserProfile
export async function fetchUserProfile(): Promise<
  | {
      rStatus: "success";
      rData: UserProfileResponse;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileGet",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<UserProfileResponse>(
        response.data,
        userProfileResponseSchema,
        "User Profile"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid user profile data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid user profile data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No user profile found"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user profile"),
    };
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user profile"),
    };
  }
}
// #endregion fetchUserProfile

// #region sendUpdateUserMarketingOptIn
export async function sendUpdateUserMarketingOptIn(optIn: boolean): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { marketingOptIn: optIn },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error("Authentication failed after updating marketing opt-in");
        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t(
            "Authentication failed after updating marketing opt-in"
          ),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Please update your password before proceeding."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update marketing opt-in status"),
    };
  } catch (error) {
    console.error("Error updating user marketing opt-in:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update marketing opt-in status"),
    };
  }
}
// #endregion sendUpdateUserMarketingOptIn

// #region sendUpdateUserPasswordReset
export async function sendUpdateUserPasswordReset(newPassword: string): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { password: newPassword },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error("Authentication failed after updating password reset");

        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t("Authentication failed after updating password reset"),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Password cannot be the same as the previous password."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update password reset"),
    };
  } catch (error) {
    console.error("Error updating user password reset:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update password reset"),
    };
  }
}
// #endregion sendUpdateUserPasswordReset

// #region sendUpdateUserCompanies
export async function sendUpdateUserCompanies(
  companies: EditableCompany[]
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const convertedCompanies =
      await convertEditableCompanyToDBExpectedFormat(companies);

    console.log("Converted Companies:", convertedCompanies);

    // if (convertedCompanies) {
    //   return {
    //     rStatus: "success",
    //   };
    // }

    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { company: convertedCompanies },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error("Authentication failed after updating user companies");

        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t("Authentication failed after updating user companies"),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Please update your password before proceeding."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user companies"),
    };
  } catch (error) {
    console.error("Error updating user companies:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user companies"),
    };
  }
}
// #endregion sendUpdateUserCompanies

// #region sendUpdateUserPreferredLanguage
export async function sendUpdateUserPreferredLanguage(
  preferredLanguage: string
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { preferredLanguage: preferredLanguage },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error(
          "Authentication failed after updating preferred language"
        );

        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t(
            "Authentication failed after updating preferred language"
          ),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Please update your password before proceeding."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user preferred language"),
    };
  } catch (error) {
    console.error("Error updating user preferred language:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update preferred language"),
    };
  }
}
// #endregion sendUpdateUserPreferredLanguage

export async function sendUpdateUserPreferredCommunicationMethods(
  preferredCommunicationMethods: string[]
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { preferredCommunicationMethods: preferredCommunicationMethods },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error(
          "Authentication failed after updating user preferred communication methods"
        );

        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t(
            "Authentication failed after updating preferred communication methods"
          ),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Please update your password before proceeding."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user preferred communication methods"),
    };
  } catch (error) {
    console.error(
      "Error updating user preferred communication methods:",
      error
    );
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update preferred communication methods"),
    };
  }
}

export async function sendUpdateUserConfirmCorrect(): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userProfileUpdate",
      { confirmCorrect: true },
      true
    );

    if (response.status === 200 || response.status === 202) {
      const authCheck = await checkAuth();
      if (!authCheck) {
        console.error("Authentication failed after confirming user details");

        window.location.href = ROUTE_LOGIN_OUT;
        return {
          rStatus: "error",
          rData: i18n.t("Authentication failed after confirming user details"),
        };
      }
      return {
        rStatus: "success",
      };
    }

    if (response.status === 304) {
      return {
        rStatus: "error",
        rData: i18n.t("Please update your password before proceeding."),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User profile not found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to confirm user details"),
    };
  } catch (error) {
    console.error("Error confirming user details:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to confirm user details"),
    };
  }
}

async function checkAuth(): Promise<boolean> {
  const authed = await authenticate(getLSRefreshToken() || "");
  if (authed.rStatus === "error") {
    console.error(
      "Authentication failed after updating user companies:",
      authed.rData
    );
    return false;
  }
  setSessionDismissedUpdateAccount(true);
  return true;
}
